import { FilterCriteriaItem, PageParam } from './appDomain';
import { MeasurementUnitEnum } from './measurementDomain';
import { BUSINESS_TYPE_ENUM, SkuGalleryLabel } from './gallerySkuDomain';
import {
  JobConfigSvcOptionType,
  JobManualSvc,
  JobOptionDef,
  SearchScopeEnum,
} from './jobconfigDomain';
import { OrgSkuInheritSettingEnum } from './orgDomain';
import { Svc, SvcType } from './svcDomain';

export interface ListSkuParam {
  page: PageParam;
  query?: SkuFilterObj;
  tenantId?: number;
}

export interface CreateSkuParam {
  isMulti: boolean;
  inheritFromId?: number;
  templateId?: number;
}

export type SkuSize = {
  /**
   * Read-only, already in the viewer's unit. The API sends it alongside the raw field,
   * which stays imperial because selections are matched and posted back on that one.
   */
  finishedSizeMetric?: string;
  flatSizeMetric?: string;

  finishedSize: string;
  flatSize: string;
  orientation: OrientationEnum;
  widthLengthSize: string;
  /**
   * The ready-made size this offering came from, e.g. `A4`.
   *
   * Only ever set on the entries in `offeredSizes*`; a size the supplier typed has no name. The
   * buyer's size step lists the two separately on the strength of this.
   */
  presetName?: string;
  /**
   * Which catalogue `presetName` came from.
   *
   * The name alone does not say: `Business Card` is in both, at 85x55mm and at 3.5x2in. Carried
   * on the entry rather than read off the reader's current unit, because the two can differ -
   * pick A4, switch to inches, and the name still came from the metric catalogue.
   */
  presetUnit?: MeasurementUnitEnum;
};

export type SkuType = {
  id: number;
  name: string;
  excelOrigin?: string;
  images?: string[];
  skuCount?: number;
  skuLabel?: SkuGalleryLabel;
  revxId?: number;
};

export type Spec = {
  key: string;
  value: string;
};

export enum InkEnum {
  black = 'black',
  cmyk = 'cmyk',
  none = 'none',
  pms = 'pms',
}

export enum InheritSettingEnum {
  EXCEPTION_AUTO = 'EXCEPTION_AUTO',
  EXCEPTION_MANUAL = 'EXCEPTION_MANUAL',
  NO_EXCEPTION = 'NO_EXCEPTION',
}

export enum CostingMethodEnum {
  C = 'C',
  SQ = 'SQ',
  U = 'U',
}

export enum MultiTypeEnum {
  BOTH = 'BOTH',
  PART = 'PART',
  STANDALONE = 'STANDALONE',
  WRAPPER = 'WRAPPER',
}

export const MultiTypeDisplay: { [key: string]: string } = {
  BOTH: 'Both(standalone and in multi-SKUs)',
  STANDALONE: 'Standalone',
  WRAPPER: 'Multi-SKU',
  PART: 'Include with Multi-SKU',
};

export enum OrientationEnum {
  landscape = 'landscape',
  portrait = 'portrait',
  cube = 'cube',
}

export enum CustomRuleCodeEnum {
  CFEBDP = 'CFEBDP',
  CFUBDP = 'CFUBDP',
  CFUP = 'CFUP',
  SFEP = 'SFEP',
  NONE = 'NONE',
}

export enum CustomPageCountRuleCodeEnum {
  R4PG = 'R4PG',
  NONE = 'NONE',
}

export enum QtyDependencyEnum {
  WITH_EVEN_PAGE_COUNT = 'WITH_EVEN_PAGE_COUNT',
  WITH_MAX_PAGE_COUNT = 'WITH_MAX_PAGE_COUNT',
  EQ_SKU_QTY = 'EQ_SKU_QTY',
  SPECIFIC = 'SPECIFIC',
}

export enum ArtworkOptionEnum {
  NONE = 'NONE',
  FIXED_JOB_ARTWORK = 'FIXED_JOB_ARTWORK',
  ARTWORK_TEMPLATE = 'ARTWORK_TEMPLATE',
  DESIGN_ON_DEMAND = 'DESIGN_ON_DEMAND',
}

export enum ParcelCategoriesEnum {
  FC = 'FC',
  LC = 'LC',
  SC = 'SC',
  T = 'T',
  CP = 'CP',
}

export type SkuPartQtyDependency = {
  dependsOnSkuId: number;
  defaultQty: number;
  qtyDependencyEnum: QtyDependencyEnum;
};

export type SkuSizePair = {
  partSize: SkuSize;
  wrapperSize: SkuSize;
};

/**
 * A size written back as the two numbers the supplier typed, in the unit their form is showing.
 *
 * The request declares `enteredUnit`, so the API stores that side verbatim and derives the other.
 * Joining the string here wrote whatever unit the page happened to be in straight into the
 * imperial field - a wrapper size typed as 215.9x279.4 was stored as 215.9x279.4 inches.
 */
export type EnteredSkuSize = Omit<SkuSize, 'finishedSize' | 'flatSize'> & {
  finishedSize?: string;
  flatSize?: string;
  finishedWidth?: number;
  finishedLength?: number;
  flatWidth?: number;
  flatLength?: number;
};

export type EnteredSkuSizePair = {
  partSize: SkuSize;
  wrapperSize: EnteredSkuSize;
};
export type SKUPart = {
  id: number;
  part: SKU;
  qtyDependency: SkuPartQtyDependency;
  sequence: number;
  skuSizePairs: SkuSizePair[];
  readonly?: boolean;
};

export type PaperDescription = {
  available: boolean;
  description: string;
  image: string;
  key: string;
  label: string;
  shortDescription: string;
  summary: string;
  value: string;
};

export type SkuPaperOption = {
  /**
   * Every basis weight as gsm, keyed by `category|weight`.
   *
   * Labels only - the raw strings stay the option values the paper steps match on.
   */
  basisWeightsMetric?: Record<string, string>;
  colorOptions: string[];
  constraint1Options: string[];
  constraint2Options: string[];
  optionCombs: SkuPaperOptionCombs[];
};

export type SkuPaperOptionCombs = {
  categoryOption: string;
  basisWeightOptions: string[];
  finishOptions: string[];
};

export type SimplifiedSKU = {
  active: boolean;
  baseRefId: number;
  id: number;
  isBaseSku: boolean;
  name: string;
  typeId: number;
  typeName: string;
};

export type SKU = SkuPresetSelection & {
  /** Metric twin, sent alongside so switching units needs no refetch. */
  defaultBleedSizeMm?: number;
  artworkOption: ArtworkOptionEnum;
  paperSearchScope?: SearchScopeEnum;
  parcelCategories: ParcelCategoriesEnum[];
  artworks: { url: string; fileName: string }[];
  impacts: [];
  optionalSvcTypes: SvcType[];
  pageCountEditable: boolean;
  requiredSvcTypes: SvcType[];
  requiredSvcs: Svc[];
  active: boolean;
  proofNeeded: boolean;
  baseRefId: number;
  bulkAllowance: number;
  coatingAllowed: string[];
  coatingBack: string[];
  coatingFront: string[];
  costingMethod: CostingMethodEnum;
  customSizeEnable: boolean;
  createTime: string;
  customRuleCode: CustomRuleCodeEnum;
  customPageCountRuleCode: CustomPageCountRuleCodeEnum;
  defaultBleedSizeIn: number;
  defaultPageCount: number;
  defaultPaperBasisWeight: string[];
  paperWeightDescription: PaperDescription[];
  defaultPaperCategory: string[];
  paperCategoryDescription: PaperDescription[];
  defaultPaperColor: string[];
  defaultPaperCount: number;
  defaultPaperFinish: string[];
  paperFinishDescription: PaperDescription[];
  defaultPaperManufacturerName: string[];
  defaultPaperName: string[];
  defaultPaperId: number;
  defaultQty: number;
  draft: boolean;
  galleryLabel: SkuGalleryLabel;
  companyLabel: SkuGalleryLabel;
  draftFromId: number;
  editing: boolean;
  useUnitPriceMatrixFixedQuantity: boolean;
  customQuantityEnabled?: boolean;
  customQuantities?: number[];
  hideFreightCost?: boolean;
  hideQuantityMultipleVersion?: boolean;
  equipmentCodes: string[];
  excelOrigin: string;
  exceptEquipmentIds: number[];
  exceptRequiredSvcIds: number[];
  exceptRequiredSvcs: Svc[];
  extraSpecs: Spec[];
  foldPanelCount: number;
  hasPmsBack: boolean;
  hasPmsFront: boolean;
  id: number;
  images: string[];
  inheritSetting: InheritSettingEnum;
  inherited: boolean;
  inheritedFromId: number;
  inheritedFromVersion: number;
  inkAllowed: InkEnum[];
  inkBack: InkEnum[];
  inkFront: InkEnum[];
  isBaseSku: boolean;
  minQty: number;
  multiPage: boolean;
  multiType: MultiTypeEnum;
  name: string;
  displayName: string;
  displayNamePlural: string;
  optionalSvcTypeIds: number[];
  orgId: number;
  orientation: OrientationEnum[];
  paperNUp: number;
  parcelCategory: string;
  parts: SKUPart[];
  proofRequired: boolean;
  requiredSvcIds: number[];
  requiredSvcTypeIds: number[];
  showInGallery: boolean;
  sides: number;
  skuSizes: SkuSize[];
  skuType: SkuType;
  taxCode: string;
  updateTime: string;
  version: number;
  weightBufferLb: number;
  readonly?: boolean;
  skuPaperOptions?: SkuPaperOption;
  revxId?: number;
  skipPageCount: boolean;
  skipSizeAndOrientation: boolean;
  skipPaperOptions: boolean;
  skipInkAndFinish: boolean;
  skipRequiredSvc: boolean;
  skipJobOption: boolean;
  skipOptionalSvc: boolean;
  skipAdditionalJobOption: boolean;
  skipArtFile: boolean;
  galleryLabelId?: number;
  companyLabelId?: number;
  topGalleryLabels?: BUSINESS_TYPE_ENUM[];
  optionalSvcGroup?: SvcGroup;
  optionalSvcDefs: JobOptionDef[];
  requiredSvcGroup?: SvcGroup;
  requiredSvcDefs: JobOptionDef[];
  jobOptions?: SvcGroup;
  jobOptionDefs: JobOptionDef[];
  additionalJobOptions?: SvcGroup;
  additionalJobOptionDefs: JobOptionDef[];
  paperEquivalentCode: string;
  svcGroupUnion: JobConfigSvcOptionType;
  requiredSvcDetails: SvcDetails[];
  optionalSvcDetails: SvcDetails[];
  additionalJobOptionDetails: SvcDetails[];
  jobOptionDetails: SvcDetails[];
  preConfigured: boolean;
  supplierPrice: UnitPriceSupplier;
  buyerPrice: UnitPriceBuyer;
  jobManualSvcs: JobManualSvc[];
  description: string;
  pmsFrontEditable: boolean;
  pmsBackEditable: boolean;
  pmsFront: string[];
  pmsBack: string[];
  qtyLocked: boolean;
  specifySupplierOrgIds: number[];
  isSpecifySupplier: boolean;
  specifySuppliers: SimpleOrgDTO[];
  hidePaperConstraintSection: boolean;
  hidePaperSearchSection: boolean;
};

export interface SimpleOrgDTO {
  id: number;
  name: string;
  companyId: number;
  companyName: string;
}

export interface SvcDetails {
  id: number;
  name: string;
  image: string;
  revxId: number;
}

export interface SvcGroup {
  svcIdGroup: SvcGroupItem;
  svcSpecIdGroup: SvcGroupItem;
  svcTypeIdGroup: SvcGroupItem;
}

export interface SvcGroupItem {
  exclusiveType: SKU_SERVICE_GENRE_ENUM;
  idGroups?: SvcGroupItem[];
  ids: number[];
}

export interface GalleryLabels {
  active: boolean;
  businessTypes: BUSINESS_TYPE_ENUM[];
  description: string;
  dropDownLabels: GalleryLabels[];
  id: number;
  image: string;
  name: string;
  parentId: number;
  selectSkuLabelName: string;
  version: number;
}

export interface SkuGalleryLabelsAll {
  galleryLabels: GalleryLabels[];
  topGalleryLabels: BUSINESS_TYPE_ENUM[];
  companyLabels: GalleryLabels[];
}

export type SKUSummary = Pick<
  SKU,
  | 'active'
  | 'id'
  | 'images'
  | 'inheritSetting'
  | 'multiType'
  | 'name'
  | 'taxCode'
  | 'version'
>;

export type PartialSKU = Partial<SKU>;

export interface SkuFilterObj {
  id?: FilterCriteriaItem;
  active?: FilterCriteriaItem;
  name?: FilterCriteriaItem;
  draft?: FilterCriteriaItem;
  isBaseSku?: FilterCriteriaItem;
  multiType?: FilterCriteriaItem;
}

export const getSkuInheritSetting = (
  sku: SKU,
  orgSkuInheritSetting: OrgSkuInheritSettingEnum
): OrgSkuInheritSettingEnum => {
  if (sku.inheritSetting === InheritSettingEnum.EXCEPTION_AUTO) {
    return OrgSkuInheritSettingEnum.AUTO;
  }
  if (sku.inheritSetting === InheritSettingEnum.EXCEPTION_MANUAL) {
    return OrgSkuInheritSettingEnum.MANUAL;
  }

  return orgSkuInheritSetting;
};

export const InheritSettingDisplay: { [key: string]: string } = {
  MANUAL: 'Manual',
  AUTO: 'Auto',
};

export type SkuPaperOptions = {
  basisWeights: string[];
  brands: string[];
  categories: string[];
  colors: string[];
  companies: string[];
  finishes: string[];
};

export type PlatformSkuPaperOptions = {
  /**
   * Each basis weight as gsm, keyed by `category|weight`.
   *
   * Labels only - the raw strings stay the option values, because the paper pickers match
   * selections on them. Keyed by grade too: 100lb is 148gsm as text and 271gsm as cover.
   */
  basisWeightsMetric?: Record<string, string>;
  basisWeights: string[];
  categories: string[];
  colors: string[];
  finishes: string[];
  constraint1Options: string[];
  constraint2Options: string[];
};

export enum StepActionTypeEnum {
  STEP0_OVERVIEW = 'STEP0_OVERVIEW',
  STEP1_QUANTITIES = 'STEP1_QUANTITIES',
  STEP2_SIZES = 'STEP2_SIZES',
  STEP3_PAPER = 'STEP3_PAPER',
  STEP4_PRESSES = 'STEP4_PRESSES',
  STEP5_UPLOADFILES = 'STEP5_UPLOADFILES',
  STEP6_INKFINISHES = 'STEP6_INKFINISHES',
  STEP7_JOBOPTIONS = 'STEP7_JOBOPTIONS',
  STEP7_REQUIREDSERVICES = 'STEP7_REQUIREDSERVICES',
  STEP8_ADDITIONNALJOBOPTIONS = 'STEP8_ADDITIONNALJOBOPTIONS',
  STEP8_OPTIONALSERVICES = 'STEP8_OPTIONALSERVICES',
  STEP9_EXTRA = 'STEP9_EXTRA',
}

export type StepActionItem = {
  [x in string]: (payload: PartialSKU) => Promise<Boolean> | undefined;
};

export type StepActionPayload = {
  [x in string]: PartialSKU;
};

export type UpdatePaper = {
  id?: number;
  defaultPaperCount: number;
  paperOptions: {
    basisWeights: string[];
    brands: string[];
    categories: string[];
    colors: string[];
    companies: string[];
    finishes: string[];
    paperIds: number[];
  };
  version?: number;
};

export enum FinishesItemTypeEnum {
  SKU_FINISHES = 'SKU_FINISHES',
  SKU_INKS = 'SKU_INKS',
  SKU_PMS = 'SKU_PMS',
}

export type FinishesItem = {
  available: Boolean;
  key: FinishesItemTypeEnum;
  label: string;
  value: string;
};

export type InkFinishes = {
  finishes: FinishesItem[];
  inks: FinishesItem[];
  pms: FinishesItem[];
};

export type UpdateInkFinishes = {
  coatingBack: string[];
  coatingFront: string[];
  hasPmsBack: Boolean;
  hasPmsFront: Boolean;
  id: number;
  inkBack: string[];
  inkFront: string[];
  version: number;
};

export type UpdatePartSize = {
  id: number;
  skuId: number;
  part: SKU;
  qtyDependency: SkuPartQtyDependency;
  sequence: number;
  skuSizePairs: SkuSizePair[];
  readonly?: boolean;
  version: number;
};

export type UpdateSize = {
  /** The unit the submitted numbers are in, as the form displayed them. */
  enteredUnit?: MeasurementUnitEnum;

  customRuleCode: CustomRuleCodeEnum;
  customSizeEnable: boolean;
  defaultBleedSizeIn: number;
  orientation: OrientationEnum[];
  foldPanelCount: number;
  skipSizeAndOrientation: boolean;
  id: number;
  skuSizes: {
    finishedLength: number;
    finishedWidth: number;
    finishedHeight?: number;
    flatLength: number;
    flatWidth: number;
    orientation: OrientationEnum;
  }[];
  /**
   * The ready-made sizes the supplier has selected, by name, one list per catalogue.
   *
   * An empty list means they cleared the selection; omitting the field leaves the stored one
   * alone. Only the list for the unit the form is in is ever edited in a given session, so the
   * other one is sent back unchanged.
   */
  presetSizesMetric?: string[];
  presetSizesImperial?: string[];
  version: number;
};

export interface UpdateSkuPartParam {
  id?: number;
  skuId?: number;
  version?: number;
}

/**
 * A ready-made finished size offered on the size step, such as A4 or Letter.
 *
 * The dimensions are in this preset's own system - a metric preset's are millimetres - and go into
 * the size form exactly as they are. The form declares which unit it is working in, so the API
 * stores that side verbatim and derives the other, the same as a size typed by hand.
 */
export type SkuPresetSize = {
  name: string;
  width: number;
  length: number;
  image?: string;
  description?: string;
};

/**
 * The names of the ready-made sizes a SKU offers, one list per catalogue.
 *
 * A reference into the dictionary rather than a copy of its dimensions, and two lists rather than
 * one because metric and imperial are different catalogues: a metric buyer is offered A4, an
 * imperial one Letter. They sit alongside `skuSizes`, which a SKU keeps offering either way.
 */
export type SkuPresetSelection = {
  presetSizesMetric?: string[];
  presetSizesImperial?: string[];
  /**
   * What a metric buyer may choose from: the ready-made sizes and the size table merged, with a
   * size that is in both appearing once.
   *
   * Both lists arrive together and the size step picks between them as the reader switches,
   * because they differ in membership rather than in formatting - metric is the A/B/C series and
   * DL, imperial is Letter, Legal, Tabloid and the Arch and ANSI sheets, and only `Business Card`
   * is in both (at two different sizes). Choosing one on the server instead froze the picker at
   * whatever unit the page loaded with, since nothing refetches on a switch: open it in inches,
   * switch to millimetres, and Letter and Legal were still the offer, restated as 215.9x279.4mm.
   *
   * A size already picked from the other list is carried across the switch by the size step, so
   * changing units cannot quietly drop a choice.
   *
   * Read-only; the size step still posts back an entry's own `finishedSize`.
   */
  offeredSizesMetric?: SkuSize[];

  /** The same for an imperial buyer. See {@link offeredSizesMetric}. */
  offeredSizesImperial?: SkuSize[];
};

/**
 * Both catalogues, sent together.
 *
 * Metric and imperial are different lists, not one converted list: a metric supplier picks A4 and
 * an imperial one picks Letter. Both arrive at once so switching units shows the other set
 * immediately, with no refetch.
 */
export type SkuPresetSizes = {
  metric: SkuPresetSize[];
  imperial: SkuPresetSize[];
};

export type SkuSizePayload = {
  finishedWidth: number;
  finishedLength: number;
  finishedHeight?: number;
  flatWidth: number;
  flatLength: number;
  orientation: OrientationEnum | undefined;
  key?: number;
};

export type SkuSizePairPayload = {
  /**
   * The unit every row in {@link updateParams} was typed in.
   *
   * Declared once for the whole batch rather than per row: `SkuPartSizeBatchUpdateParams` hands
   * it down to each one, and the wizard states it once for the submission.
   */
  enteredUnit?: MeasurementUnitEnum;

  wrapperSkuId: number;
  updateParams: {
    skuId: number;
    id: number;
    skuSizePairs: EnteredSkuSizePair[];
  }[];
};

export const QtyDependencyDisplayMap: { [index: string]: string } = {
  [QtyDependencyEnum.WITH_EVEN_PAGE_COUNT]: 'Based on SKU Quantity',
  [QtyDependencyEnum.WITH_MAX_PAGE_COUNT]: 'Based on SKU Page Count - Max',
  [QtyDependencyEnum.EQ_SKU_QTY]: 'Based on SKU Page Count - Equal',
  [QtyDependencyEnum.SPECIFIC]: 'Specific Quantity',
};

export enum SvcShowTypeEnum {
  JOB_OPTIONS = 'JOB_OPTIONS',
  ADDITIONAL_JOB_OPTIONS = 'ADDITIONAL_JOB_OPTIONS',
  OPTIONAL = 'OPTIONAL',
  REQUIRED = 'REQUIRED',
}

export enum SvcGroupLevelEnum {
  SVC_TYPE = 'SVC_TYPE',
  SVC = 'SVC',
  SVC_SPEC = 'SVC_SPEC',
}

export enum SKU_SERVICE_TYPE_ENUM {
  SERVICE_TYPE = 'SERVICE_TYPE',
  SERVICE = 'SERVICE',
  SERVICE_SPEC = 'SERVICE_SPEC',
}

export enum SKU_SERVICE_GENRE_ENUM {
  ONE_OF = 'ONE_OF',
  ANY_OF = 'ANY_OF',
}

export interface SkuAddFilesPayload {
  id: number;
  version: number;
  artworkOption: ArtworkOptionEnum;
  fixedArtworkProofRequired: boolean;
  artworks: Partial<{ url: string }>[];
}

export enum UnitPricePricingModel {
  PRICING_ENGINE = 'PRICING_ENGINE',
  UNIT_PRICE = 'UNIT_PRICE',
}

export enum UnitPricePricingMethod {
  QUANTITY_RANGE = 'QUANTITY_RANGE',
  FIXED_QUANTITY = 'FIXED_QUANTITY',
}

export interface UnitPriceSupplierTier {
  option: UnitPriceSupplierTiersOption;
  orgId: number | undefined;
  quantityRanges?: UnitPriceSupplierTierRange[];
  fixedQuantity?: UnitPriceSupplierFixedQuantity[];
}

export enum UnitPriceSupplierTiersOption {
  STANDALONE = 'STANDALONE',
  CONTINUOUS = 'CONTINUOUS',
}

export enum SkuDynamicTemplateType {
  FRONT = 'FRONT',
  BACKEND = 'BACKEND',
}

export interface UnitPriceSupplierTierRange {
  highest: number;
  lowest: number;
  priceToPaySupplier: number;
  pricingEnginePrice: number | null;
  id: number;
}

export interface UnitPriceSupplierFixedQuantity {
  quantity: number;
  /* empty when the supplier does not price that quantity - the column is left blank in the grid */
  priceToPaySupplier: number | null;
  pricingEnginePrice: number | null;
  id: number;
}

/* the supplier to buy one fixed quantity from, picked on the supplier price screen.
   A quantity without an entry falls back to the cheapest supplier, like before this was on screen. */
export interface UnitPricePreferredSupplier {
  quantity: number | null;
  orgId?: number;
}

export interface UnitPriceSupplier {
  createAt: string;
  maximumQuantity: number;
  pricingModel: UnitPricePricingModel;
  pricingMethod: UnitPricePricingMethod;
  skuId: number;
  suppliers: UnitPriceSupplierTier[];
  preferredSuppliers?: UnitPricePreferredSupplier[];
  updateAt: string;
}

export interface UnitPriceSupplierParams {
  maximumQuantity: number;
  pricingModel: UnitPricePricingModel;
  suppliers: UnitPriceSupplierTier;
}

export interface UnitPriceSupplierList {
  orgId: number;
  orgName: string;
}

export interface UnitPriceBuyerRange {
  highest: number;
  lowest: number;
  markup: number;
  option: UnitPriceBuyerRangeOption;
  priceEach: number;
  supplierOrgIds: number[];
  priceEachFromSupplierPrice: number | null;
  markupValue: number;
  // id: number;
}

export interface UnitPriceBuyerFixedQuantity {
  quantity: number;
  markup: number;
  option: UnitPriceBuyerRangeOption;
  priceEach: number;
  supplierOrgIds: number[];
  priceEachFromSupplierPrice: number | null;
  markupValue: number;
  previousPrice: number | null;
  previousPriceMarkup: number | null;
  previousPriceDiff: number | null;
}

export enum UnitPriceBuyerRangeOption {
  NO_ADDITIONAL = 'NO_ADDITIONAL',
  MARKUP_MARKDOWN = 'MARKUP_MARKDOWN',
  PRICING_ENGINE = 'PRICING_ENGINE',
}

export interface UnitPriceBuyer {
  createAt: string;
  defaultGlobalMarkupPct: number;
  quantityRanges: UnitPriceBuyerRange[];
  pricingMethod: UnitPricePricingMethod;
  fixedQuantity: UnitPriceBuyerFixedQuantity[];
  sendToRfm: boolean;
  skuId: number;
  maximumQuantity: number;
  updateAt: string;
}

export interface UnitPriceBuyerParams {
  quantityRanges: UnitPriceBuyerRange[];
  sendToRfm: boolean;
}

export type DynamicParamType =
  | 'TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'DROPDOWN'
  | 'EMAIL'
  | 'TELL'
  | 'TEL'
  | 'FAX'
  | 'CELL'
  | 'WEBSITE'
  | 'PHONE'
  | 'COMPOSITE';

export type DynamicParamAlignType = 'LEFT' | 'CENTER' | 'RIGHT';

export type DynamicParamDropdownType =
  | 'STATIC'
  | 'SPRING'
  | 'HTTP'
  | 'MULTIPLE_FIELD';

export type DynamicCustomerFieldOverLengthStrategy =
  | 'IGNORE'
  | 'REDUCE_FONT'
  | 'REDUCE_FONT_ERROR'
  | 'REDUCE_FONT_NEW_LINE'
  | 'NEW_LINE';

export interface DynamicParamDropDownInfoDTO {
  dropdownType?: DynamicParamDropdownType;
  options?: Record<string, string>;
  multiFieldInfos?: MultiFieldInfoDTO[];
  multiFieldOptions?: Record<string, Record<string, string>>;
}

export interface MultiFieldInfoDTO {
  name?: string;
  font?: string;
  bold?: boolean;
  fontSize?: number;
  x?: number;
  y?: number;
  maxLength?: number;
  overLengthStrategy?: DynamicCustomerFieldOverLengthStrategy;
}

export interface DynamicParamQuantityInfoDTO {
  dynamicParamType?: DynamicParamType;
  dropDownInfo?: DynamicParamDropDownInfoDTO;
}

export interface DynamicCustomerFieldDTO {
  index?: number;
  name?: string;
  title?: string;
  type?: DynamicParamType;
  align?: DynamicParamAlignType;
  font?: string;
  bold?: boolean;
  fontSize?: number;
  x?: number;
  y?: number;
  maxLength?: number;
  defaultValue?: string;
  dropDownInfo?: DynamicParamDropDownInfoDTO;
  multiFieldInfos: MultiFieldInfoDTO[];
  overLengthStrategy?: DynamicCustomerFieldOverLengthStrategy;
  subFields?: SubFieldDTO[];
  compositeFields?: SubFieldDTO[];
  description?: string;
  required?: boolean;
}

export interface SubFieldDTO {
  condition?: string;
  title?: string;
  type?: DynamicParamType;
  description?: string;
  appendText?: string;
  defaultValue?: string;
}

export interface SkuDynamicTemplateDTO {
  id?: number;
  templateName?: string;
  templateUrl?: string;
  templateType?: SkuDynamicTemplateType;
  originalTemplateUrl?: string;
  originalImageTemplateUrl?: string;
  customerFields?: DynamicCustomerFieldDTO[];
}

export interface SkuDynamicTemplateMappingDTO {
  skuId?: number;
  frontTemplateDTO?: SkuDynamicTemplateDTO;
  backendTemplateDTO?: SkuDynamicTemplateDTO;
  quantityConfig?: DynamicParamQuantityInfoDTO;
}

export interface SkuDynamicTemplateMappingSimpleDTO {
  skuId?: number;
  frontTemplateId?: number;
  backendTemplateId?: number;
  categoryId?: number;
}

export interface SkuDynamicTemplateCategoryDTO {
  id?: number;
  orgId?: number;
  categoryName?: string;
  galleryLabelId?: string;
  galleryLabelName?: string;
  companyLabelId?: number;
  companyLabelName?: string;
}
